import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { IconMenu2, IconX, IconArrowLeft } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { DocumentationSidebar } from './components/DocumentationSidebar';
import { SearchBox } from './components/SearchBox';
import { DocumentationContentWithTOC } from './components/DocumentationContentWithTOC';
import { useDocumentation } from './hooks/useDocumentation';
import useAuthStore from '@/store/AuthStore';
import Routes from '@/router/routes';

/**
 * Documentation page component
 *
 * @description
 * Standalone documentation page with sidebar navigation, search, TOC, and markdown content.
 * Features include:
 * - Collapsible sidebar with nested navigation
 * - Full-text search with debouncing
 * - Breadcrumb navigation
 * - Table of contents with active section highlighting
 * - Back to dashboard navigation
 * - Responsive design with mobile overlay
 * - Loading states and error handling
 * - Theme-aware styling
 * - Lazy-loaded syntax highlighting
 *
 * @example
 * ```tsx
 * // In your router configuration
 * <Route path="/documentation/:docPath*" element={<Documentation />} />
 * ```
 */
function Documentation() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { authToken } = useAuthStore();

  // React Router v6 splat routes use "*" as the key
  const docPath = params['*'] || params.docPath;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authToken) {
      navigate(Routes.login.path, { replace: true });
    }
  }, [authToken, navigate]);

  // Default to README.md if no docPath is provided
  // Add .md extension back if not present (URLs don't have extension, but file fetching needs it)
  const currentDocPath = (() => {
    if (!docPath) return 'README.md';
    return docPath.endsWith('.md') ? docPath : `${docPath}.md`;
  })();

  // Fetch current document
  const { document, isLoading, error, isNotFound, refetch } = useDocumentation(currentDocPath);

  // Navigate back to workspace (main dashboard)
  const handleBackToDashboard = () => {
    const fallback_organization = Cookies.get('main_organization');
    const fallback_workspace = Cookies.get('fallback_workspace');

    if (fallback_workspace && fallback_organization) {
      navigate(
        `${Routes.organization.path}/${fallback_organization}${Routes.workspace.path}/${fallback_workspace}`,
      );
    } else {
      // Fallback to home if context is not available
      navigate(Routes.home.path);
    }
  };

  // Don't render if not authenticated
  if (!authToken) {
    return null;
  }

  return (
    <div
      style={{ backgroundColor: currentTheme?.background, color: currentTheme?.['primary-text'] }}
      className="flex min-h-screen"
    >
      {/* Sidebar with navigation */}
      <DocumentationSidebar
        organizationId=""
        currentDocPath={currentDocPath}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <main className="flex-1 min-w-0">
        {/* Mobile header with toggle and search */}
        <div
          className="sticky top-0 z-40 border-b px-4 py-3 md:hidden"
          style={{
            backgroundColor: currentTheme?.background,
            borderColor: currentTheme?.['border-color'],
          }}
        >
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <IconX size={20} /> : <IconMenu2 size={20} />}
            </Button>
            <div className="flex-1">
              <SearchBox organizationId="" />
            </div>
          </div>
        </div>

        {/* Desktop header with back button and search */}
        <div
          className="hidden md:block sticky top-0 z-40 border-b px-6 py-4"
          style={{
            backgroundColor: currentTheme?.background,
            borderColor: currentTheme?.['border-color'],
          }}
        >
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToDashboard}
              className="flex items-center gap-2"
            >
              <IconArrowLeft size={18} />
              Back to Dashboard
            </Button>
            <div className="flex-1">
              <SearchBox organizationId="" />
            </div>
          </div>
        </div>

        {/* Document Content with TOC and Breadcrumbs */}
        <DocumentationContentWithTOC
          doc={document}
          loading={isLoading}
          error={error}
          organizationId=""
          docPath={currentDocPath}
          showToc
          onRetry={refetch}
          notFound={isNotFound}
          onGoHome={() => navigate(Routes.documentation.path)}
        />
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default Documentation;
